import Picture from "@/models/Picture";
import getImageSortValue from "@/helpers/getImageSortValue";
import { IMAGE_SRC_TYPES } from "@/config/image";
import getTextFromHtmlString from "@/helpers/getTextFromHtmlString";

export class Image {

  static NON_WORD_CHARS = [
    '«', '»', '“', '”', '"', '`', '¨', '\'',
    '\\', '/', '|',
    '(', ')', '[', ']', '{', '}', '<', '>',
    '?', '!', ',', '.', ':', ';', '-',
    '*', '%', '&', '#', '=', '_', '^', '¬', '~', '§', '°'
  ].reduce((acc, curr) => (acc[curr] = true, acc), {});

  constructor(data, coordinate, hasCoordinateManualOrder) {
    this.builder = data.builder
    this.company = data.company
    this.description = data.description
    this.id = data.id
    this.srcType = data.src_type
    this.src = data.src
    this.srcNumber = data.src_number
    this.subImages = data.sub_images.map((subImage) => new Picture(subImage, data))
    this.type = data.type
    this.yearOfBuild = data.year_of_build || 0
    this.yearOfRebuild = data.year_of_rebuild || 0
    this.isCover = data.is_cover
    this.manualOrder = data.order

    this.coordinateId = coordinate.id
    this.street = coordinate.street
    this.region = coordinate.region
    this.num = coordinate.num
    this.streetLabel = `${ this.street?.name || '' } ${ this.num || '' }`
    this.streetLabelText = getTextFromHtmlString(this.streetLabel)

    const keywordsText = getTextFromHtmlString(data.description).concat(' ').concat(data.company || '');
    const keywordsTextWithNoHtmlEntities = keywordsText
      .replaceAll('&nbsp;', ' ')
      .replaceAll('&lt;', ' ')
      .replaceAll('&gt;', ' ')
      .replaceAll('&amp;', ' ')
      .replaceAll('&quot;', ' ')
      .replaceAll('&apos;', ' ')
      .replaceAll('&laquo;', ' ')
      .replaceAll('&raquo;', ' ')
      .replaceAll('&dash;', ' ')
      .replaceAll('&hyphen;', ' ')
      .replaceAll('&ndash;', ' ')
      .replaceAll('&mdash;', ' ')
      .replaceAll('&minus;', ' ')
      .trim();
    const keywordsTextWithNoSpecialCharacters = [...keywordsTextWithNoHtmlEntities]
      .map(letter => Image.NON_WORD_CHARS[letter] ? ' ' : letter)
      .join('');
    this.keyWords = data.description
      ? [...new Set(keywordsTextWithNoSpecialCharacters
        .split(/\s+/)
        .map(word => word.trim())
        .filter(word => word.length > 2))
      ]
      : []

    this.pictures = [
      ...(this.srcType === IMAGE_SRC_TYPES.YOUTUBE
        ? []
        : data.src.map((src, index) => {
            return new Picture(
              {
                id: `main-picture_${this.id}-${index}`,
                src,
                src_number: this.srcNumber,
                src_type: this.srcType,
              },
              data
            );
          })),
      ...this.subImages
        .map((subImage) => {
          return subImage.src.map((subImageSrc, subImageSrcIndex) => {
            return new Picture(
              {
                id: `sub-image-${this.id}_${subImage.id}-${subImageSrcIndex}`,
                src: subImageSrc,
                src_number: subImage.src_number,
                src_type: subImage.src_type,
              },
              data
            );
          });
        })
        .flat(),
    ];

    if (this.srcType === IMAGE_SRC_TYPES.YOUTUBE) {
      this.pictures.unshift(new Picture({
        id: `main-picture-youtube_${ this.id }`,
        src: this.srcNumber,
        src_number: this.srcNumber,
        src_type: this.srcType
      }, data))
    }

    this.titlePicture = this.pictures[0] || null

    this.epoch = data.epoch
    this.isEternal = !(this.epoch ?? false)

    this.sortValue = getImageSortValue(this, hasCoordinateManualOrder);
  }

  get eternal() {
    return !this.epoch;
  }

  get fromYear() {
    return this.epoch?.from || null
  }

  get toYear() {
    return this.epoch?.to || null
  }

}

export default Image